import React, { useCallback } from 'react';
import MaterialTable from '@material-table/core';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Chip } from '@mui/material';
import { SmscModel, SmscObject } from '../../../../models/smscModel';
import { convertVersionableIdToString, VersionableId } from '../../../../types/versionableId';
import AddBoxIcon from '@mui/icons-material/AddBox';

type Props = {
  readonly smscList: SmscModel[];
  readonly isLoading: boolean;
  readonly onAddClick: () => void;
  readonly onViewClick: (smscId: VersionableId) => void;
  readonly onEditClick: (smscId: VersionableId) => void;
  readonly onDeleteClick: (smscId: VersionableId) => void;
};

type SmscRow = SmscObject & {
  readonly id: string;
  readonly versionableId: VersionableId;
};

const SmscTable = (props: Props) => {
  const {
    smscList,
    isLoading,
    onAddClick,
    onViewClick,
    onEditClick,
    onDeleteClick,
  } = props;

  const smscRows: SmscRow[] = smscList.map(smsc => ({
    id: convertVersionableIdToString(smsc.id),
    versionableId: smsc.id,
    ...smsc.object,
  }));

  const renderConnectionsCount = useCallback((smscRow: SmscRow) => {
    return <Chip label={smscRow.connections.length} />;
  }, []);

  return (
    <MaterialTable
      title={
        <Typography component='span' color='textSecondary' gutterBottom>
          <Typography component='span' variant='h5'>
            SMSCs
          </Typography>
        </Typography>
      }
      data={smscRows}
      isLoading={isLoading}
      columns={[
        {
          title: 'Id',
          field: 'versionableId.resourceName',
        },
        {
          title: 'SMSC ID',
          field: 'smscId',
          defaultSort: 'asc',
        },
        {
          title: 'Log file',
          field: 'logFile',
        },
        {
          title: 'Log level',
          field: 'logLevel',
        },
        {
          title: 'Connection(s)',
          type: 'numeric',
          render: renderConnectionsCount,
        },
      ]}
      actions={[
        {
          icon: () => <AddBoxIcon color='primary' />,
          tooltip: 'Add smsc',
          isFreeAction: true,
          onClick: () => onAddClick(),
        },
        smscRow => ({
          icon: () => <VisibilityIcon color='primary' />,
          tooltip: 'View smsc',
          onClick: () => onViewClick(smscRow.versionableId),
        }),
        smscRow => ({
          icon: () => <EditIcon color='primary' />,
          tooltip: 'Edit smsc',
          onClick: () => onEditClick(smscRow.versionableId),
        }),
        smscRow => ({
          icon: () => <DeleteIcon color='primary' />,
          tooltip: 'Remove smsc',
          onClick: () => onDeleteClick(smscRow.versionableId),
        }),
      ]}
      options={{
        padding: 'dense',
        selection: false,
        pageSizeOptions: [10, 25, 50],
        pageSize: 10,
        actionsColumnIndex: 5,
        headerStyle: {
          fontWeight: 'bold',
        },
      }}
      components={{
        Container: props => <Paper elevation={1} {...props} />
      }}
    />
  )
}

export default SmscTable;
